<template>
    <div class="container min-h-screen bg-[#11111b] text-white flex flex-col">
        <Header />
        
        <!-- Hero Section with Profile Image -->
        <section class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-6 md:gap-12">
                <div v-if="isLoading" class="text-white text-left space-y-6 w-full md:w-2/3">
                    <h2 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold">About Me</h2>
                    <p class="text-base sm:text-lg md:text-xl leading-relaxed">Loading...</p>
                </div>
                <div v-else-if="error" class="text-white space-y-3 w-full md:w-2/3">
                    <p class="text-base sm:text-lg md:text-xl leading-relaxed text-red-600">{{ error }}</p>
                </div>
                <div v-else class="text-white space-y-3 w-full md:w-2/3">
                    <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">About Me</h3>
                    <p class="text-base sm:text-lg md:text-xl leading-relaxed text-justify">
                        {{ aboutData.aboutTxt || 'No description available.' }}
                    </p>
                    
                    <!-- Personal Information -->
                    <div v-if="hasPersonalInfo" class="mt-6">
                        <ul class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm sm:text-base md:text-lg">
                            <li v-if="computedAge !== 'N/A'" class="flex items-center">
                                <span class="text-md text-gray-400 font-medium mr-2">Age:</span> {{ computedAge }} 
                            </li>
                            <li v-if="aboutData.gender" class="flex items-center gap-2">
                                <span class="text-md text-gray-400 font-medium mr-2">Gender:</span> {{ aboutData.gender }}
                            </li>
                            <li v-if="aboutData.nationality" class="flex items-center gap-2">
                                <span class="text-md text-gray-400 font-medium mr-2">Nationality:</span> {{ aboutData.nationality }}
                            </li>
                            <li v-if="aboutData.available !== null" class="flex items-center gap-2">
                                <span class="text-md text-gray-400 font-medium mr-2">Available for Hire:</span> 
                                <span :class="aboutData.available ? 'text-green-400' : 'text-red-400'">
                                    {{ aboutData.available }}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
                
                <div class="w-full max-w-[200px] sm:max-w-[300px] md:max-w-[350px] h-[200px] sm:h-[300px] md:h-[350px] overflow-hidden relative shrink-0"
                    v-if="aboutData">
                    <img :src="aboutData.imageURL || '/images/borenn1.png'" alt="Borenn"
                        class="w-full h-full object-cover rounded-full"
                        @error="handleHeroImageError">
                    <div class="absolute inset-0 bg-[#11111b] opacity-40"></div>
                </div>
                <div v-else>
                    <img src="../assets/images/borenn1.png" alt="Default Borenn"
                        class="w-full max-w-[200px] sm:max-w-[300px] md:max-w-[350px] h-[200px] sm:h-[300px] md:h-[350px] object-cover rounded-full">
                    <div class="absolute inset-0 bg-[#11111b] opacity-40"></div>
                </div>
            </div>
        </section>

        <!-- Statistics Section -->
        <section v-if="hasStats" class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-5xl mx-auto">
                <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Statistics</h3>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div v-if="aboutData.projectsCount" class="text-center">
                        <div class="text-3xl font-bold text-cyan-400">{{ aboutData.projectsCount }}+</div>
                        <div class="text-gray-400">Projects Completed</div>
                    </div>
                    <div v-if="aboutData.experienceYears" class="text-center">
                        <div class="text-3xl font-bold text-cyan-400">{{ aboutData.experienceYears }}+</div>
                        <div class="text-gray-400">Years Experience</div>
                    </div>
                    <div v-if="aboutData.clientsCount" class="text-center">
                        <div class="text-3xl font-bold text-cyan-400">{{ aboutData.clientsCount }}+</div>
                        <div class="text-gray-400">Happy Clients</div>
                    </div>
                    <div v-if="aboutData.certificationsCount" class="text-center">
                        <div class="text-3xl font-bold text-cyan-400">{{ aboutData.certificationsCount }}+</div>
                        <div class="text-gray-400">Certifications</div>
                    </div>
                </div>
            </div>
        </section>


        <!-- Testimonials Section -->
        <section v-if="hasTestimonials" class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-7xl mx-auto">
                <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Clients</h3>
                <div class="testimonials-carousel-wrapper" :class="{ 'overflow-x-auto': !shouldAnimate }" ref="carouselWrapper">
                    <div class="testimonials-carousel" 
                        :class="{ 'animated': shouldAnimate }"
                        :style="shouldAnimate ? {} : { animation: 'none' }">
                        <div v-for="(testimonial, index) in (shouldAnimate ? duplicatedTestimonials : (aboutData.testimonials || []).filter(t => t.imageURL))" 
                            :key="shouldAnimate ? `${index}-${testimonial.name}` : `img-${index}`" 
                            class="testimonial-card flex-shrink-0">
                            <img :src="testimonial.imageURL" :alt="testimonial.name || 'Client'" 
                                class="w-24 h-24 object-contain"
                                @error="handleTestimonialImageError" />
                        </div>
                    </div>
                </div>
            </div>
        </section>


       

       

        <!-- Skills Section -->
        <section v-if="hasSkills" class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-5xl mx-auto flex flex-col gap-6">
                <div v-if="isLoading" class="text-white text-left space-y-6 w-full">
                    <p class="text-base sm:text-lg md:text-xl leading-relaxed">Loading...</p>
                </div>
                <div v-else-if="error" class="text-white text-left space-y-4 w-full">
                    <p class="text-base sm:text-lg md:text-xl leading-relaxed text-red-600">{{ error }}</p>
                </div>
                <div v-else class="text-white text-left space-y-6 w-full">
                    <div class="space-y-8">
                        <div v-if="aboutData.frontends && aboutData.frontends.length > 0">
                            <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Frontend</h3>
                            <ul class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                <li v-for="skill in aboutData.frontends" :key="skill"
                                    class="card px-3 py-2 text-xs sm:text-sm md:text-base flex items-center gap-2">
                                    <img v-if="logoMap[skill]" :src="logoMap[skill]" :alt="skill + ' logo'"
                                        class="w-5 h-5 object-contain" :style="logoColors[skill] || ''">
                                    <span>{{ skill }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-if="aboutData.backends && aboutData.backends.length > 0">
                            <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Backend</h3>

                            <ul class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                <li v-for="skill in aboutData.backends" :key="skill"
                                    class="card px-3 py-2 text-xs sm:text-sm md:text-base flex items-center gap-2">
                                    <img v-if="logoMap[skill]" :src="logoMap[skill]" :alt="skill + ' logo'"
                                        class="w-5 h-5 object-contain" :style="logoColors[skill] || ''">
                                    <span>{{ skill }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-if="aboutData.databases && aboutData.databases.length > 0">
                            <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Databases</h3>

                            <ul class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                <li v-for="skill in aboutData.databases" :key="skill"
                                    class="card px-3 py-2 text-xs sm:text-sm md:text-base flex items-center gap-2">
                                    <img v-if="logoMap[skill]" :src="logoMap[skill]" :alt="skill + ' logo'"
                                        class="w-5 h-5 object-contain" :style="logoColors[skill] || ''">
                                    <span>{{ skill }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-if="aboutData.tools && aboutData.tools.length > 0">
                            <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Development Tools</h3>
                            <ul class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                <li v-for="skill in aboutData.tools" :key="skill"
                                    class="card px-3 py-2 text-xs sm:text-sm md:text-base flex items-center gap-2">
                                    <img v-if="logoMap[skill]" :src="logoMap[skill]" :alt="skill + ' logo'"
                                        class="w-5 h-5 object-contain" :style="logoColors[skill] || ''">
                                    <span>{{ skill }}</span>
                                </li>
                            </ul>
                        </div>
                        <div v-if="aboutData.graphics && aboutData.graphics.length > 0">
                                <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Graphics</h3>
                            <ul class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                <li v-for="skill in aboutData.graphics" :key="skill"
                                    class="card px-3 py-2 text-xs sm:text-sm md:text-base flex items-center gap-2">
                                    <img v-if="logoMap[skill]" :src="logoMap[skill]" :alt="skill + ' logo'"
                                        class="w-5 h-5 object-contain" :style="logoColors[skill] || ''">
                                    <span>{{ skill }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Experience Section -->
        <section v-if="hasExperience" class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-5xl mx-auto">
                <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Work Experience</h3>
                <div class="space-y-8">
                    <div v-for="(exp, index) in aboutData.experience" :key="index" class="card p-6">
                        <div class="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                            <h4 class="text-xl font-semibold text-cyan-400">{{ exp.title }}</h4>
                            <div class="text-gray-400 text-sm">
                                {{ formatDate(exp.startDate) }} - {{ exp.endDate ? formatDate(exp.endDate) : 'Present' }}
                            </div>
                        </div>
                        <div class="text-lg font-medium mb-2">{{ exp.company }}</div>
                        <p class="text-gray-300">{{ exp.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Education Section -->
        <section v-if="hasEducation" class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-5xl mx-auto">
                <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Education</h3>
                <div class="space-y-8">
                    <div v-for="(edu, index) in aboutData.education" :key="index" class="card p-6">
                        <div class="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                            <h4 class="text-xl font-semibold text-cyan-400">{{ edu.degree }}</h4>
                            <div class="text-gray-400 text-sm">
                                {{ formatDate(edu.startDate) }} - {{ edu.endDate ? formatDate(edu.endDate) : 'Present' }}
                            </div>
                        </div>
                        <div class="text-lg font-medium mb-2">{{ edu.institution }}</div>
                        <p class="text-gray-300">{{ edu.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Achievements Section -->
        <section v-if="hasAchievements" class="py-12 px-4 sm:px-6 md:px-12 bg-[#11111b] border-t border-gray-800">
            <div class="max-w-5xl mx-auto">
                <h3 class="text-1xl sm:text-2xl md:text-3xl lg:text-3xl font-semibold mb-8 text-left text-cyan-400">Achievements & Certifications</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div v-for="(achievement, index) in aboutData.achievements" :key="index" class="card p-6">
                        <div class="flex items-center justify-between mb-3">
                            <h4 class="text-lg font-semibold text-cyan-400">{{ achievement.title }}</h4>
                            <span class="text-gray-400 text-sm">{{ formatDate(achievement.date) }}</span>
                        </div>
                        <p class="text-gray-300">{{ achievement.description }}</p>
                    </div>
                </div>
            </div>
        </section>

        
        <ClientFooter />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { getDocs, collection, query, limit } from 'firebase/firestore';
import Header from '@/components/Header.vue';
import ClientFooter from '@/components/ClientFooter.vue';
import { Phone, Mail, MapPin, Github, Linkedin, Facebook, MessageCircle } from 'lucide-vue-next';
import { projectFirestore } from '@/firebase/config';

const isLoading = ref(true);
const error = ref(null);
const aboutData = ref(null);
const carouselWrapper = ref(null);
const shouldAnimate = ref(false);

// Computed properties to check if sections have data
const hasPersonalInfo = computed(() => {
    return aboutData.value && (
        computedAge.value !== 'N/A' ||
        aboutData.value.gender ||
        aboutData.value.nationality ||
        aboutData.value.available !== null
    );
});

const hasStats = computed(() => {
    return aboutData.value && (
        aboutData.value.projectsCount ||
        aboutData.value.experienceYears ||
        aboutData.value.clientsCount ||
        aboutData.value.certificationsCount
    );
});

const hasContactInfo = computed(() => {
    return aboutData.value && (
        aboutData.value.phone ||
        aboutData.value.email ||
        aboutData.value.address
    );
});

const hasSocialLinks = computed(() => {
    return aboutData.value && (
        aboutData.value.githubURL ||
        aboutData.value.linkedinURL ||
        aboutData.value.facebookURL ||
        aboutData.value.telegramURL
    );
});

const hasSkills = computed(() => {
    return aboutData.value && (
        (aboutData.value.frontends && aboutData.value.frontends.length > 0) ||
        (aboutData.value.backends && aboutData.value.backends.length > 0) ||
        (aboutData.value.databases && aboutData.value.databases.length > 0) ||
        (aboutData.value.tools && aboutData.value.tools.length > 0) ||
        (aboutData.value.graphics && aboutData.value.graphics.length > 0)
    );
});

const hasExperience = computed(() => {
    return aboutData.value && aboutData.value.experience && aboutData.value.experience.length > 0;
});

const hasEducation = computed(() => {
    return aboutData.value && aboutData.value.education && aboutData.value.education.length > 0;
});

const hasAchievements = computed(() => {
    return aboutData.value && aboutData.value.achievements && aboutData.value.achievements.length > 0;
});

const hasTestimonials = computed(() => {
    return aboutData.value && 
           aboutData.value.testimonials && 
           Array.isArray(aboutData.value.testimonials) &&
           aboutData.value.testimonials.some(t => t.imageURL);
});

// Duplicate testimonials for seamless carousel loop (only those with images)
const duplicatedTestimonials = computed(() => {
    if (!aboutData.value || !Array.isArray(aboutData.value.testimonials)) return [];
    // Filter only testimonials with images
    const testimonialsWithImages = aboutData.value.testimonials.filter(t => t.imageURL);
    // Duplicate the array 3 times for seamless infinite scroll
    return [...testimonialsWithImages, ...testimonialsWithImages, ...testimonialsWithImages];
});

// Check if testimonials overflow and need animation
const checkTestimonialsOverflow = () => {
    if (!carouselWrapper.value || !aboutData.value || !Array.isArray(aboutData.value.testimonials)) {
        shouldAnimate.value = false;
        return;
    }
    
    // Calculate if testimonials fit on screen
    const wrapper = carouselWrapper.value;
    const wrapperWidth = wrapper.offsetWidth;
    const cardWidth = 120; // width of testimonial card (image only)
    const gap = 24; // margin-right (1.5rem = 24px)
    const testimonialsWithImages = aboutData.value.testimonials.filter(t => t.imageURL).length;
    const totalTestimonialsWidth = testimonialsWithImages * (cardWidth + gap);
    
    // Enable animation only if testimonials overflow the screen
    shouldAnimate.value = totalTestimonialsWidth > wrapperWidth;
};

// Helper function to format dates
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
    });
};

// Map technology names to logo URLs
const logoMap = computed(() => ({
    'HTML': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/61/HTML5_logo_and_wordmark.svg/1024px-HTML5_logo_and_wordmark.svg.png',
    'CSS': 'https://upload.wikimedia.org/wikipedia/commons/d/d5/CSS3_logo_and_wordmark.svg',
    'JavaScript': 'https://static.vecteezy.com/system/resources/previews/027/127/463/non_2x/javascript-logo-javascript-icon-transparent-free-png.png',
    'Vue JS': 'https://www.golden-team.org/static/services/vuejs.webp',
    'Flutter': 'https://img.icons8.com/color/512/flutter.png',
    'Firebase': 'https://vectorseek.com/wp-content/uploads/2025/05/Firebase-icon-Logo-PNG-SVG-Vector.png',
    'Node JS': 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Node.js_logo.svg/2560px-Node.js_logo.svg.png',
    'Mongodb': 'https://infinapps.com/wp-content/uploads/2018/10/mongodb-logo.png',
    'NoSQL': 'https://www.stackery.io/assets/images/posts/stackery-support-for-nosql-tabes/featured.png',
    'Adobe Photoshop': 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/af/Adobe_Photoshop_CC_icon.svg/500px-Adobe_Photoshop_CC_icon.svg.png',
    'Figma': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/33/Figma-logo.svg/1200px-Figma-logo.svg.png',
    'Tailwind': 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Tailwind_CSS_Logo.svg/2560px-Tailwind_CSS_Logo.svg.png',
    'Git': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3f/Git_icon.svg/2048px-Git_icon.svg.png',
    'Github': 'https://img.icons8.com/ios11/512/FFFFFF/github.png',
    'VSCode': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9a/Visual_Studio_Code_1.35_icon.svg/2048px-Visual_Studio_Code_1.35_icon.svg.png',
    'Canva': 'https://freepnglogo.com/images/all_img/1691829322canva-app-logo-png.png',
}));

// Map technology names to original brand colors via inline CSS
const logoColors = computed(() => ({
    'HTML': 'fill: #E34F26',
    'CSS': 'fill: #1572B6',
    'JavaScript': 'fill: #F7DF1E',
    'Vue JS': 'fill: #42B883',
    'Flutter': 'fill: #02569B',
    'Firebase': 'fill: #FFCA28',
    'Node JS': 'fill: #339933',
    'Mongodb': 'fill: #47A248',
    'NoSQL': 'fill: #47A248',
    'Adobe Photoshop': 'fill: #31A8FF',
    'Figma': 'fill: #0ACF83'
}));

const fetchAboutData = async () => {
    try {
        const aboutQuery = query(collection(projectFirestore, 'heroes'), limit(1));
        const querySnapshot = await getDocs(aboutQuery);
        if (!querySnapshot.empty) {
            aboutData.value = querySnapshot.docs[0].data();
        } else {
            error.value = 'No data found in heroes collection.';
            console.log('No heroes found.');
        }
    } catch (err) {
        error.value = 'Failed to fetch data: ' + err.message;
        console.error('Error fetching heroes:', err);
    } finally {
        isLoading.value = false;
    }
};

const handleHeroImageError = (event) => {
    // Fallback to default image if hero image fails to load
    const defaultImage = '/images/borenn1.png';
    if (event.target.src !== defaultImage && !event.target.src.includes('borenn1.png')) {
        event.target.src = defaultImage;
    } else {
        // If default also fails, hide the image
        event.target.style.display = 'none';
    }
};

const handleTestimonialImageError = (event) => {
    // Hide the testimonial image if it fails to load
    event.target.style.display = 'none';
    // Optionally remove it from the array to update the computed property
    if (aboutData.value && Array.isArray(aboutData.value.testimonials)) {
        const index = Array.from(event.target.closest('.testimonial-card')?.parentElement?.children || []).indexOf(event.target.closest('.testimonial-card'));
        if (index !== -1 && aboutData.value.testimonials[index]) {
            aboutData.value.testimonials[index].imageURL = null;
        }
    }
};

const computedAge = computed(() => {
    if (!aboutData.value || !aboutData.value.age) return 'N/A';
    const dob = new Date(aboutData.value.age);
    if (isNaN(dob.getTime())) return 'N/A';
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age >= 0 ? age : 'N/A';
});

onMounted(async () => {
    await fetchAboutData();
    
    // Check overflow after data loads and DOM updates
    await new Promise(resolve => setTimeout(resolve, 100));
    checkTestimonialsOverflow();
    
    // Recheck on window resize
    window.addEventListener('resize', checkTestimonialsOverflow);

    const sections = document.querySelectorAll('section');
    const navLinks = document.querySelectorAll('.nav-link');
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const sectionId = entry.target.id;
                    navLinks.forEach((link) => {
                        link.classList.toggle('active', link.getAttribute('data-section') === sectionId);
                    });
                    if (sectionId) {
                        window.history.replaceState(null, null, `#${sectionId}`);
                    }
                }
            });
        },
        { root: null, threshold: 0.5 }
    );

    sections.forEach((section) => observer.observe(section));

    onUnmounted(() => {
        window.removeEventListener('resize', checkTestimonialsOverflow);
        sections.forEach((section) => observer.unobserve(section));
    });
});
</script>

<style scoped>
.container {
    width: 100%;
    max-width: none;
    min-height: 100vh;
}

.card {
    background-color: #1e1e2f;
    border: 2px dashed #00b4d8;
    border-radius: 8px;
    padding: 8px;
    color: #ffffff;
    text-align: left;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

/* Testimonials Carousel Styles */
.testimonials-carousel-wrapper {
    width: 100%;
    position: relative;
}

.testimonials-carousel-wrapper.overflow-x-auto {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
}

.testimonials-carousel-wrapper.overflow-x-auto::-webkit-scrollbar {
    height: 8px;
}

.testimonials-carousel-wrapper.overflow-x-auto::-webkit-scrollbar-track {
    background: #1e1e2f;
    border-radius: 4px;
}

.testimonials-carousel-wrapper.overflow-x-auto::-webkit-scrollbar-thumb {
    background: #00b4d8;
    border-radius: 4px;
}

.testimonials-carousel-wrapper.overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #00a0c0;
}

.testimonials-carousel-wrapper:not(.overflow-x-auto) {
    overflow: hidden;
}

.testimonials-carousel {
    display: flex;
    width: max-content;
}

.testimonials-carousel.animated {
    animation: slideLeft 30s linear infinite;
}

@keyframes slideLeft {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(calc(-33.333% - 1rem));
    }
}

/* Pause animation on hover */
.testimonials-carousel-wrapper:not(.overflow-x-auto):hover .testimonials-carousel.animated {
    animation-play-state: paused;
}

.testimonial-card {
    width: 120px;
    height: 120px;
    margin-right: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .testimonial-card {
        width: 100px;
        height: 100px;
        margin-right: 1rem;
    }
    
    .testimonial-card img {
        width: 100px;
        height: 100px;
    }
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 180, 216, 0.3);
}

.card img {
    filter: none;
}
</style>